import type { ReactNode } from 'react';
import { cn } from './cn';

export interface MetaGroupProps {
  /** Section label — ABOUT, INTENT, HEALTH, LOCATION, AVAILABILITY, RATES, SERVICES. */
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * A grouped block of profile metadata.
 *
 * This fixes the same defect on both surfaces from opposite directions.
 * `.app` ran tribe / relationship / looking-for / status / last-tested /
 * distance down one undifferentiated column, so users had to decode it.
 * `.men` separated every single row with a hairline divider, which reads as
 * database output rather than a considered panel.
 *
 * The fix in both cases is grouping and space, not more rules. Sections are
 * separated by generous whitespace; rows inside a section are separated by
 * nothing at all.
 */
export const MetaGroup = ({ title, children, className }: MetaGroupProps) => (
  <section className={cn('flex flex-col gap-3', className)}>
    <h2 className="font-display text-[10px] font-bold uppercase leading-none tracking-hottr-marker text-white/48">
      {title}
    </h2>
    <div className="flex flex-col gap-2.5">{children}</div>
  </section>
);

export interface MetaRowProps {
  label: string;
  value: ReactNode;
  /** Pull the eye to a headline number — use once per group at most. */
  emphasis?: boolean;
  className?: string;
}

/**
 * Label-left, value-right. Tabular figures so prices and distances align down
 * the column without a table.
 */
export const MetaRow = ({ label, value, emphasis, className }: MetaRowProps) => (
  <div className={cn('flex items-baseline justify-between gap-6', className)}>
    <span className="font-body text-[13px] font-normal leading-[1.5] text-white/48">{label}</span>
    <span
      className={cn(
        'text-right font-body leading-[1.5] tabular-nums',
        emphasis
          ? 'text-[15px] font-extrabold text-white'
          : 'text-[13px] font-normal text-white/[0.88]',
      )}
    >
      {value}
    </span>
  </div>
);

/** Free-text block inside a group — bios, notes. */
export const MetaProse = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn('max-w-[62ch] hottr-body', className)}>{children}</p>
);

/** Wraps sections with the ecosystem's standard vertical rhythm. */
export const MetaStack = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('flex flex-col gap-8', className)}>{children}</div>
);
